import { useMemo } from "react";

/**
 * ETSGauge — animated circular gauge (0-1000).
 * Colour maps to grade: Diamond > Platinum > Gold > Silver > Bronze > NeedsImprovement.
 */
export default function ETSGauge({ score = 0, size = 180, stroke = 12, label = true }) {
  const clamped = Math.max(0, Math.min(1000, score));
  const pct = clamped / 1000;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);

  const grade = useMemo(() => {
    if (clamped >= 950) return { name: "Diamond", color: "#06B6D4", glow: "rgba(6,182,212,0.35)" };
    if (clamped >= 900) return { name: "Platinum", color: "#64748B", glow: "rgba(100,116,139,0.35)" };
    if (clamped >= 850) return { name: "Gold", color: "#C9A968", glow: "rgba(201,169,104,0.45)" };
    if (clamped >= 800) return { name: "Silver", color: "#94A3B8", glow: "rgba(148,163,184,0.35)" };
    if (clamped >= 750) return { name: "Bronze", color: "#CD7F32", glow: "rgba(205,127,50,0.35)" };
    return { name: "Needs Improvement", color: "#EF4444", glow: "rgba(239,68,68,0.35)" };
  }, [clamped]);

  return (
    <div className="inline-flex flex-col items-center gap-2" data-testid="ets-gauge">
      <div style={{ width: size, height: size, position: "relative" }}>
        <svg width={size} height={size}>
          <defs>
            <linearGradient id={`ets-grad-${size}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={grade.color} stopOpacity="1" />
              <stop offset="100%" stopColor={grade.color} stopOpacity="0.6" />
            </linearGradient>
            <filter id={`ets-glow-${size}`}>
              <feGaussianBlur stdDeviation="4" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={`url(#ets-grad-${size})`}
            strokeWidth={stroke}
            strokeDasharray={c}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dashoffset 1s cubic-bezier(.19,1,.22,1)", filter: `url(#ets-glow-${size})` }}
          />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div className="emp-eyebrow" style={{ opacity: 0.7 }}>ETS</div>
          <div style={{ fontSize: size * 0.22, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em", color: grade.color }}>
            {clamped}
          </div>
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>/ 1000</div>
        </div>
      </div>
      {label && (
        <span className={`emp-grade emp-grade-${grade.name.replace(/\s/g, "")}`}>{grade.name}</span>
      )}
    </div>
  );
}
